// Projects the entered World's accent inputs onto the document root as the
// contract's --ncl-world-accent-* variables. Only the two INPUTS are ever
// held; colors are re-derived by @neucleos/theme for the viewer's current
// appearance (design law: store inputs, resolve at read). Leaving every
// World clears the variables so Local carries no World's face.
import {
  resolveWorldTheme,
  WORLD_THEME_CSS_VARIABLES,
  type WorldThemeProfile,
} from "@neucleos/theme";
import { useEffect } from "react";

import { readWorldTheme, type WorldThemeOutcome } from "./worldThemeClient";

let projectedProfile: WorldThemeProfile | null = null;

function currentAppearance(): "light" | "dark" {
  return typeof document !== "undefined" && document.documentElement.classList.contains("dark")
    ? "dark"
    : "light";
}

export function applyWorldThemeVariables(profile: WorldThemeProfile): void {
  projectedProfile = profile;
  const resolved = resolveWorldTheme(profile, currentAppearance());
  for (const [variable, value] of Object.entries(resolved.cssVariables)) {
    document.documentElement.style.setProperty(variable, value);
  }
}

export function clearWorldThemeVariables(): void {
  projectedProfile = null;
  for (const variable of Object.values(WORLD_THEME_CSS_VARIABLES)) {
    document.documentElement.style.removeProperty(variable);
  }
}

/**
 * Apply one read outcome without borrowing authority from any other owner.
 * A refusal clears a possibly stale World face, but deliberately leaves the
 * canonical app/user theme alone; TH7 guarantees that base is always total.
 */
export function applyWorldThemeOutcome(outcome: WorldThemeOutcome): void {
  if (!outcome.ok) {
    clearWorldThemeVariables();
    return;
  }
  applyWorldThemeVariables({
    accentHue: outcome.accentHue,
    accentChroma: outcome.accentChroma,
  });
}

/**
 * The one appearance re-derivation seam: the app toggles the root `dark`
 * class, and the projected inputs re-resolve to that appearance's colors.
 */
function watchAppearance(): () => void {
  const observer = new MutationObserver(() => {
    if (projectedProfile) applyWorldThemeVariables(projectedProfile);
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => observer.disconnect();
}

/**
 * Mounted once by the World frame: entering a World reads its theme through
 * the grant door and projects it; leaving (Local, sign-out, refusal) clears.
 * A refused or unreachable read clears the World-owned variables. The total
 * canonical stock/user palette remains visible; absence never becomes a
 * transparent color and never manufactures a World profile.
 */
export function useWorldThemeProjection(worldKey: string | null): void {
  useEffect(() => {
    if (!worldKey) {
      clearWorldThemeVariables();
      return;
    }
    let cancelled = false;
    void readWorldTheme(worldKey).then((outcome) => {
      if (cancelled) return;
      applyWorldThemeOutcome(outcome);
    });
    const unwatch = watchAppearance();
    return () => {
      cancelled = true;
      unwatch();
      clearWorldThemeVariables();
    };
  }, [worldKey]);
}
